import { useState, ChangeEvent, useEffect, useRef } from "react";
import { usePodcastProvider, PodcastConfig } from "./PodcastProvider";
import { getHosts, filterFunction } from "services/utils/filter";
import { getPodcastThemeToken } from "services/utils/getPodcastThemeToken";

//ui
import { Button, Box, Flex, Input, InputLeftElement, InputGroup, Text, CheckboxGroup, Stack, useOutsideClick } from "@chakra-ui/react";
import { CustomCheckBox } from "./CustomForms/CustomCheckBox";
import { SearchIcon, FilterIcon, ChevronDownIcon } from "components/icons";
import PodcastColorTypes from "types/podcastColorTypes";

enum SelectState {
  NewestFirst = "NewestFirst",
  OldestFirst = "OldestFirst",
  AlphabeticalOrder = "AlphabeticalOrder",
  Length = "Length"
}

export function PodcastFilter() {
  const ref = useRef(null);
  const [openSelect, setOpenSelect] = useState<boolean>(false);
  const [openFilter, setOpenFilter] = useState<boolean>(false);
  const [openFilterOptions, setOpenFilterOptions] = useState<boolean>(false);
  const [lengthFilter, setLengthFilter] = useState<string[]>([]);
  const [hostFilter, setHostFilter] = useState<string[]>([]);
  const [releaseDateFilter, setReleaseDateFilter] = useState<string[]>([]);
  const [selectState, setSelectState] = useState<string>("");
  const [inputFilterData, setInputFilterData] = useState<PodcastConfig[]>([]);
  const [inputData, setInputData] = useState<string>("");

  useOutsideClick({
    ref: ref,
    handler: () => setOpenSelect(false)
  });

  const { podcastData, setFilteredData } = usePodcastProvider();

  const uniqueHosts = getHosts(podcastData);

  let allDataFilter: PodcastConfig[] = [];

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputData(e.currentTarget.value);
    const inputFilteredData: PodcastConfig[] | null = filterFunction({ podcastData: podcastData.slice(1), value: e.currentTarget.value, filterBy: "title" });
    setInputFilterData(inputFilteredData);
  };

  useEffect(() => {
    // get data filtered by input data
    const initialData = inputData ? inputFilterData : podcastData.slice(1);

    // sort the data according to options;
    const SortData = () => {
      switch (selectState) {
        case SelectState.NewestFirst:
          return initialData;
        case SelectState.OldestFirst:
          return initialData.reverse();
        case SelectState.AlphabeticalOrder:
          return initialData.sort(function (a, b) {
            if (a.title < b.title) {
              return -1;
            }
            if (a.title > b.title) {
              return 1;
            }
            return 0;
          });
        case SelectState.Length:
          return initialData.sort((a, b) => Number(a.length) - Number(b.length));
        default:
          return initialData;
      }
    };

    if (lengthFilter.length === 0 && releaseDateFilter.length === 0 && hostFilter.length === 0) {
      const SortedData = SortData();
      setFilteredData(SortedData);
    } else {
      const sortedData = SortData();

      // filter data according to length;
      lengthFilter.map(x => {
        let value: PodcastConfig[] | null = null;
        switch (x) {
          case "900000":
            value = filterFunction({ podcastData: sortedData, value: "900000", filterBy: "length", sign: "less" });
            if (value) allDataFilter.push(...value);
            break;
          case "1800000":
            value = filterFunction({ podcastData: sortedData, value: "1800000", lowerBoundary: 900000, filterBy: "length", sign: "between" });
            if (value) allDataFilter.push(...value);
            break;
          case "2700000":
            value = filterFunction({ podcastData: sortedData, value: "2700000", lowerBoundary: 1800000, filterBy: "length", sign: "between" });
            if (value) allDataFilter.push(...value);
            break;
          case "2701000":
            value = filterFunction({ podcastData: sortedData, value: "2701000", filterBy: "length", sign: "greater" });
            if (value) allDataFilter.push(...value);
            break;
        }
      });
      let FilteredLengthData: PodcastConfig[] | null;
      if (lengthFilter.length > 0) {
        FilteredLengthData = allDataFilter;
      } else {
        FilteredLengthData = sortedData;
      }
      allDataFilter = [];

      // Filter data according to host
      hostFilter.map(x => {
        const hostFilterValue = filterFunction({ podcastData: FilteredLengthData, value: x, filterBy: "host" });
        if (hostFilterValue) allDataFilter.push(...hostFilterValue);
      });
      let FilteredHostData: PodcastConfig[] | null;
      if (hostFilter.length > 0) {
        FilteredHostData = allDataFilter;
      } else {
        FilteredHostData = FilteredLengthData;
      }
      allDataFilter = [];

      // filter data according to release Date
      releaseDateFilter.map(x => {
        const presentDate = new Date();
        const releaseDateLowerLimit = (Number(presentDate) - Number(x) * 86400000) / 1000;
        const releaseDateValue = filterFunction({ podcastData: FilteredHostData, value: `${releaseDateLowerLimit}`, filterBy: "created", sign: "greater" });
        if (releaseDateValue) allDataFilter.push(...releaseDateValue);
      });
      if (releaseDateFilter.length > 0) {
        const uniqueData = allDataFilter.filter((a, i) => allDataFilter.findIndex(s => a.title === s.title) === i);
        setFilteredData(uniqueData);
      } else {
        setFilteredData(FilteredHostData);
      }
    }
  }, [lengthFilter, hostFilter, releaseDateFilter, selectState, inputFilterData, podcastData]);

  const FilterDetails = [
    {
      name: "By Length",
      options: [
        {
          name: "<15 min",
          value: "900000",
          setCheckBoxState: setLengthFilter,
          checkBoxState: lengthFilter
        },
        {
          name: "15-30 min",
          value: "1800000",
          setCheckBoxState: setLengthFilter,
          checkBoxState: lengthFilter
        },
        {
          name: "30-45 min",
          value: "2700000",
          setCheckBoxState: setLengthFilter,
          checkBoxState: lengthFilter
        },
        {
          name: "45 min+",
          value: "2701000",
          setCheckBoxState: setLengthFilter,
          checkBoxState: lengthFilter
        }
      ]
    },
    {
      name: "By Host",
      options: [
        ...uniqueHosts.map(x => {
          return {
            name: x,
            value: x,
            setCheckBoxState: setHostFilter,
            checkBoxState: hostFilter
          };
        })
      ]
    },
    {
      name: "By Release Date",
      options: [
        {
          name: "In the last 24 hours",
          value: "1",
          setCheckBoxState: setReleaseDateFilter,
          checkBoxState: releaseDateFilter
        },
        {
          name: "In the last week",
          value: "7",
          setCheckBoxState: setReleaseDateFilter,
          checkBoxState: releaseDateFilter
        },
        {
          name: "In the last 30 days",
          value: "30",
          setCheckBoxState: setReleaseDateFilter,
          checkBoxState: releaseDateFilter
        },
        {
          name: "In the last quarter",
          value: "120",
          setCheckBoxState: setReleaseDateFilter,
          checkBoxState: releaseDateFilter
        },
        {
          name: "In the last year",
          value: "365",
          setCheckBoxState: setReleaseDateFilter,
          checkBoxState: releaseDateFilter
        }
      ]
    }
  ];

  return (
    <Flex
      px={{ base: "39px", sm: "32px" }}
      pb="32px"
      fontFamily="sora"
      fontSize="16px"
      maxWidth="1384px"
      width="100%"
      margin="auto"
      fontWeight="20px"
      lineHeight="20px"
      letterSpacing="-0.3px"
      flexWrap={{ base: "wrap", md: "nowrap" }}
    >
      <Box bg="purple.150" color="purple.200" width="100%" maxWidth={{ base: "100%", md: "536px" }} borderRadius="8px" marginRight="24px">
        <InputGroup alignItems="center">
          <InputLeftElement height="100%" pointerEvents="none" children={<SearchIcon height="24px" width="24px" marginRight="8px" marginLeft="16px" color="purple.200" />} />
          <Input
            type="text"
            placeholder="Search"
            height="46px"
            borderRadius="8px"
            color="purple.200"
            bg="purple.150"
            _placeholder={{ color: "inherit" }}
            _focus={{ outlineColor: "#6B5FA5" }}
            onChange={handleInputChange}
          />
        </InputGroup>
      </Box>
      <Box width="auto" position="relative" mt={{ base: "18px", md: "0" }}>
        <Flex
          bg="purple.150"
          height="48px"
          color="purple.200"
          width="100%"
          minWidth="200px"
          borderRadius="8px"
          justifyContent="space-between"
          alignItems="center"
          px="16px"
          onClick={e => {
            setOpenSelect(!openSelect);
          }}
        >
          <Box>{selectState ? <Text color="purple.200">{selectState}</Text> : <Text color="purple.200"> Sort By:</Text>}</Box>
          <Box>
            <ChevronDownIcon width="48px" height="48px" />
          </Box>
        </Flex>
        <Box ref={ref} position="absolute" width="100%" top="58px" bg="purple.150" color="purple.200" py="8px" borderRadius="12px" zIndex="20" display={openSelect ? "block" : "none"}>
          <CustomCheckBox value={SelectState.NewestFirst} label="Newest First" setSelectState={setSelectState} selectState={selectState} />
          <CustomCheckBox value={SelectState.OldestFirst} label="Oldest First" setSelectState={setSelectState} selectState={selectState} />
          <CustomCheckBox value={SelectState.AlphabeticalOrder} label="Alphabetical order" setSelectState={setSelectState} selectState={selectState} />
          <CustomCheckBox value={SelectState.Length} label="Length" setSelectState={setSelectState} selectState={selectState} />
        </Box>
      </Box>
      <Button
        display={{ base: "flex", md: "none" }}
        mt={{ base: "18px", md: "0" }}
        color={getPodcastThemeToken(PodcastColorTypes.Icons)}
        backgroundColor={getPodcastThemeToken(PodcastColorTypes.IconsBackground)}
        width="48px"
        ml={{ base: "15px", md: "24px" }}
        height="48px"
        borderRadius="50%"
        _hover={{ bg: "blue.90" }}
        onClick={e => {
          setOpenFilterOptions(!openFilterOptions);
        }}
      >
        <FilterIcon width="48px" height="48px" />
      </Button>

      <Flex ml={{ md: "24px" }} width={{ base: "100%", md: "auto" }} position="relative">
        <Flex
          display={{ base: "none", md: `${openFilter ? "flex" : "none"}` }}
          bg="blue.90"
          height="48px"
          color="purple.200"
          width="100%"
          minWidth="312px"
          borderRadius="8px"
          justifyContent="space-between"
          alignItems="center"
          px="16px"
          onClick={e => {
            setOpenFilterOptions(!openFilterOptions);
          }}
        >
          <Box>
            <Text color="purple.200">Filter By</Text>
          </Box>
          <Box>
            <ChevronDownIcon width="48px" height="48px" />
          </Box>
        </Flex>
        <Box
          position="absolute"
          width="100%"
          minWidth={{ base: "100%", md: "316px" }}
          maxWidth={{ base: "100%", md: "316px" }}
          top={{ base: "10px", md: "58px" }}
          bg="blue.90"
          color="purple.100"
          py="16px"
          borderRadius="12px"
          zIndex="20"
          display={openFilterOptions ? "block" : "none"}
        >
          {FilterDetails.map(item => {
            return (
              <CheckboxGroup key={item.name}>
                <Text px="16px" my="8px" fontWeight="700" color="purple.100">
                  {item.name}
                </Text>
                <Stack mt={1} spacing="2px">
                  {item.options.map(({ name, value, setCheckBoxState, checkBoxState }) => {
                    return <CustomCheckBox key={value} value={value} label={name} setCheckBoxState={setCheckBoxState} checkBoxState={checkBoxState} />;
                  })}
                </Stack>
              </CheckboxGroup>
            );
          })}
        </Box>
      </Flex>
      <Button
        display={{ base: "none", md: "flex" }}
        mt={{ base: "18px", md: "0" }}
        bgColor="blue.90"
        width="48px"
        ml={{ base: "15px", md: "24px" }}
        height="48px"
        borderRadius="50%"
        color="purple.50"
        _hover={{ bg: "blue.90" }}
        onClick={e => {
          setOpenFilter(!openFilter);
          setOpenFilterOptions(false);
        }}
      >
        <FilterIcon width="32px" height="32px" />
      </Button>
    </Flex>
  );
}
